import { type PolygonIdentifier } from "../types";

import type maplibregl from "maplibre-gl";

type SourceDataHandler = (e: maplibregl.MapSourceDataEvent) => void;

const pendingHandlersByMap = new WeakMap<maplibregl.Map, Map<string, SourceDataHandler>>();

function buildPendingKey(sourceId: string, featureId: string, sourceLayerId?: string): string {
  return `${sourceId}::${sourceLayerId ?? ""}::${featureId}`;
}

function cancelPendingHandler(map: maplibregl.Map, pendingKey: string): void {
  const handlers = pendingHandlersByMap.get(map);
  if (!handlers) return;

  const existing = handlers.get(pendingKey);
  if (!existing) return;

  map.off("sourcedata", existing);
  handlers.delete(pendingKey);
}

/**
 * Safely sets feature state on the map.
 *
 * Uses `identifier.value` directly as the feature ID for MapLibre's feature state,
 * rather than relying on `feature.id`. This ensures unique feature state management
 * even when multiple features share the same `feature.id` (common with vector tiles).
 *
 * Pending `sourcedata` listeners for the same feature are cancelled when a new state
 * is requested, so a deferred `{ active: true }` cannot overwrite a later `{ active: false }`.
 *
 * Only defers when the source is not ready yet. Surviving GeoJSON `setData` wipes is
 * handled by selection highlight sync (`useBoundaryCombineHighlight`), not here.
 *
 * @param map - The MapLibre map instance
 * @param sourceId - The source ID containing the feature
 * @param identifier - Polygon identifier with key and value
 * @param state - The state object to set on the feature
 * @param sourceLayerId - Optional source layer ID for vector sources
 */
export function setFeatureStateSafe(
  map: maplibregl.Map | null,
  sourceId: string,
  identifier: PolygonIdentifier,
  state: Record<string, unknown>,
  sourceLayerId?: string,
): void {
  if (!map) return;

  const featureId = String(identifier.value);
  const pendingKey = buildPendingKey(sourceId, featureId, sourceLayerId);

  cancelPendingHandler(map, pendingKey);

  const target: maplibregl.FeatureIdentifier = {
    source: sourceId,
    id: featureId,
    ...(sourceLayerId ? { sourceLayer: sourceLayerId } : {}),
  };

  const apply = (): boolean => {
    if (!map.getStyle()) return false;
    if (!map.getSource(sourceId)) return false;

    try {
      map.setFeatureState(target, state);
      return true;
    } catch (error) {
      console.error("Failed to set feature state:", error);
      return false;
    }
  };

  if (apply()) return;

  const handleSourceData: SourceDataHandler = (e) => {
    if (e.sourceId !== sourceId) return;
    if (apply()) {
      cancelPendingHandler(map, pendingKey);
    }
  };

  const handlers = pendingHandlersByMap.get(map) ?? new Map<string, SourceDataHandler>();
  handlers.set(pendingKey, handleSourceData);
  pendingHandlersByMap.set(map, handlers);
  map.on("sourcedata", handleSourceData);
}
